import { ProCard, ProTable } from '@ant-design/pro-components';
import { useQuery } from '@tanstack/react-query';
import { useSetState } from 'ahooks';
import { Avatar, Button, DatePicker, Flex, Form, Input, Select, Space, Typography } from 'antd';
import { get, groupBy, isEqual, mapValues } from 'lodash';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * APIs
 */
import { systemLogOptions, systemLogs } from '@/apis/system-log';

/**
 * Types
 */
import type { QuerySystemLogDto } from '@/dtos/system-log.dto';
import type { ActionType, ProTableProps } from '@ant-design/pro-components';
import type { FormProps, PaginationProps } from 'antd';

/**
 * Constants
 */
import { RANGE_PICKER_PRESETS } from '@/constants/presets';

const defaultPayload: QuerySystemLogDto = {
    action: undefined,
    biz_id: undefined,
    ip_fuzzy: undefined,
    module: undefined,
    ua_fuzzy: undefined,
    user_id: undefined,
    created_at: undefined,
};

const PageSystemLogs: React.FC = () => {
    /**
     * Hooks
     */
    const [formSearch] = Form.useForm();

    const tableRef = useRef<ActionType>();

    const { t } = useTranslation();

    /**
     * Paginations
     */
    const [pagination, setPagination] = useSetState<PaginationProps>({
        current: 1,
        pageSize: 20,
        showQuickJumper: true,
        showSizeChanger: true,
        onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
    });

    /**
     * Payloads
     */
    const [payload, setPayload] = useSetState<QuerySystemLogDto>({
        ...defaultPayload,
    });

    /**
     * Requests
     */
    const {
        data: dataSystemLogs,
        isLoading: loadingSystemLogs,
        refetch: refetchSystemLogs,
    } = useQuery({
        queryKey: ['system-logs', pagination.current, pagination.pageSize, payload],
        retry: false,
        queryFn: () => systemLogs({ page: pagination.current, size: pagination.pageSize, ...payload }),
    });

    const { data: dataSystemLogOptions, isLoading: loadingSystemLogOptions } = useQuery({
        queryKey: ['system-log-options'],
        retry: false,
        queryFn: () => systemLogOptions(),
    });

    /**
     * ChildrenProps
     */
    const formSearchProps: FormProps = {
        form: formSearch,
        initialValues: defaultPayload,
        layout: 'inline',
        onFinish: (values) => {
            values.created_at = [
                values.created_at?.[0].startOf('day').format(),
                values.created_at?.[1].endOf('day').format(),
            ];

            const assignPayload = {
                ...defaultPayload,
                ...mapValues(values, (value) => (value === '' ? undefined : value)),
            };

            if (isEqual(payload, assignPayload)) {
                return refetchSystemLogs();
            }

            setPagination({
                current: 1,
            });

            setPayload(assignPayload);
        },
    };

    const proTableProps: ProTableProps<any, QuerySystemLogDto> = {
        actionRef: tableRef,
        bordered: true,
        columns: [
            {
                dataIndex: 'created_at',
                title: t('action_at'),
                valueType: 'dateTime',
                width: 200,
            },
            {
                dataIndex: 'user_id',
                title: t('user'),
                width: 300,
                render: (_, entity) =>
                    entity.user ? (
                        <Space align="center">
                            <Avatar
                                alt=""
                                src={entity.user.avatar && `${import.meta.env.VITE_CDN}/${entity.user.avatar}`}
                            >
                                {entity.user.nickname?.charAt(0).toUpperCase()}
                            </Avatar>

                            <Typography.Text copyable={{ text: entity.user_id }}>
                                {entity.user.nickname} (@{entity.user.username})
                            </Typography.Text>
                        </Space>
                    ) : (
                        '-'
                    ),
            },
            {
                dataIndex: 'module',
                title: t('module'),
                width: 300,
            },
            {
                dataIndex: 'action',
                title: t('action'),
                width: 100,
            },
            {
                dataIndex: 'status',
                title: t('status'),
                valueEnum: {
                    Failure: {
                        status: 'Error',
                        text: t('status_failure'),
                    },
                    Init: {
                        disabled: true,
                        status: 'Default',
                        text: t('status_init'),
                    },
                    Success: {
                        status: 'Success',
                        text: t('status_success'),
                    },
                },
                width: 100,
            },
            {
                dataIndex: 'ip',
                title: 'IP',
                width: 200,
            },
            {
                dataIndex: 'path',
                ellipsis: true,
                title: 'Path',
                renderText: (text, record) => `${record.method} ${text}`,
            },
            {
                fixed: 'right',
                key: 'option',
                title: t('actions'),
                valueType: 'option',
                width: 200,
            },
        ],
        dataSource: get(dataSystemLogs, 'data.data', []),
        loading: loadingSystemLogs,
        options: {
            reload: () => refetchSystemLogs(),
        },
        pagination: {
            ...pagination,
            total: get(dataSystemLogs, 'data.meta.total', 0),
        },
        rowKey: 'id',
        scroll: {
            x: 1700,
        },
        search: false,
    };

    const moduleOptions = useMemo(() => {
        // 1. 解析
        const parsed = get(dataSystemLogOptions, 'data.data.modules', []).map((str) => {
            const [parent, child] = str.split('_');

            return {
                parent,
                child,
            };
        });

        // 2. 分组
        const grouped = groupBy(parsed, 'parent');

        // 3. 转成 select 可用结构
        return Object.entries(grouped)
            .sort()
            .map(([parent, children]) => ({
                label: parent,
                options: children.sort().map((c) => ({ label: c.child, value: `${c.parent}_${c.child}` })),
            }));
    }, [get(dataSystemLogOptions, 'data.data.modules', [])]);

    return (
        <>
            <ProCard className="mb-16">
                <Form {...formSearchProps}>
                    <Flex gap={16} wrap="wrap">
                        <Form.Item label={t('action_at')} name="created_at" style={{ marginRight: 0 }}>
                            <DatePicker.RangePicker allowClear={true} presets={RANGE_PICKER_PRESETS(t)} />
                        </Form.Item>

                        <Form.Item name="user_id" style={{ marginRight: 0 }}>
                            <Input allowClear={true} placeholder={t('user_id')} style={{ width: 200 }} />
                        </Form.Item>

                        <Form.Item name="biz_id" style={{ marginRight: 0 }}>
                            <Input allowClear={true} placeholder={t('biz_id')} style={{ width: 200 }} />
                        </Form.Item>

                        <Form.Item name="module" style={{ marginRight: 0 }}>
                            <Select
                                allowClear={true}
                                loading={loadingSystemLogOptions}
                                options={moduleOptions}
                                placeholder={t('module')}
                                style={{ width: 200 }}
                            />
                        </Form.Item>

                        <Form.Item name="action" style={{ marginRight: 0 }}>
                            <Select
                                allowClear={true}
                                loading={loadingSystemLogOptions}
                                options={get(dataSystemLogOptions, 'data.data.actions', [])
                                    .sort()
                                    .map((action) => ({
                                        label: action,
                                        value: action,
                                    }))}
                                placeholder={t('actions')}
                                style={{ width: 100 }}
                            />
                        </Form.Item>

                        <Form.Item name="ip_fuzzy" style={{ marginRight: 0 }}>
                            <Input allowClear={true} placeholder="IP" style={{ width: 200 }} />
                        </Form.Item>

                        <Button
                            loading={loadingSystemLogs}
                            type="primary"
                            onClick={() => {
                                formSearch.submit();
                            }}
                        >
                            {t('search')}
                        </Button>

                        <Button
                            onClick={() => {
                                formSearch.resetFields();
                            }}
                        >
                            {t('reset')}
                        </Button>
                    </Flex>
                </Form>
            </ProCard>

            <ProTable {...proTableProps} />
        </>
    );
};

export default PageSystemLogs;
